import React from 'react';
import { Button } from 'rebass';
import { Input, Label, Textarea } from '@rebass/forms';

import NiceModal from 'components/NiceModal';
import { useForm } from 'hooks/useForm';
import { useToastHelper } from 'hooks/useToastHelper';
import { useSubmitProductFeedbackMutation } from 'api/useSubmitProductFeedbackMutation';
import services from 'services/services';

/**
 * Modal for submitting product feedback to Airtable.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is visible
 * @param {Function} props.onClose - Called when modal is dismissed
 */
export function ProductFeedbackModal({ isOpen, onClose }) {
    const { register, handleSubmit, reset } = useForm({
        initialForm: { feedbackTitle: '', feedbackText: '' }
    });
    const { addSuccessToast, addErrorToast } = useToastHelper();

    const { mutate: submitFeedback, isPending } = useSubmitProductFeedbackMutation({
        onSuccess: () => {
            addSuccessToast('Thank you for your feedback!');
            reset();
            onClose();
        },
        onError: error => {
            services.parseAndTrackXhrErrors(error);
            addErrorToast('Failed to submit feedback. Please try again.');
        }
    });

    const onSubmit = handleSubmit(data => {
        submitFeedback({
            feedbackTitle: data.feedbackTitle,
            feedbackText: data.feedbackText,
            url: window.location.href
        });
    });

    const onCancel = () => {
        reset();
        onClose();
    };

    return (
        <NiceModal
            isOpen={isOpen}
            shouldCloseOnOverlayClick
            onRequestClose={onCancel}
            style={{ content: { width: 'auto', maxWidth: '600px' } }}
            title="Product Feedback"
        >
            <p className="color-text-secondary mb-6">
                We'd love to hear your feedback about the product. Let us know what's working well or what could be
                improved.
            </p>
            <form id="product-feedback-form" onSubmit={onSubmit}>
                <div className="mb-4">
                    <Label htmlFor="product-feedback-title" className="color-text-primary fs-14 bold !mb-1">
                        Title
                    </Label>
                    <Input
                        {...register('feedbackTitle')}
                        id="product-feedback-title"
                        autoFocus
                        className="theme-input"
                        placeholder="Brief summary of your feedback"
                        required
                    />
                </div>
                <div className="mb-4">
                    <Label htmlFor="product-feedback-text" className="color-text-primary fs-14 bold !mb-1">
                        Details
                    </Label>
                    <Textarea
                        {...register('feedbackText')}
                        id="product-feedback-text"
                        className="theme-input"
                        sx={{ minHeight: '160px' }}
                        placeholder="Please describe your feedback in detail..."
                        required
                    />
                </div>
            </form>
            <div className="modal-actions">
                <Button type="button" onClick={onCancel} variant="secondary-gray" className="!mr-2">
                    Cancel
                </Button>
                <Button
                    type="submit"
                    variant="primary"
                    disabled={isPending}
                    className="modal-primary !m-0"
                    form="product-feedback-form"
                >
                    {isPending ? 'Submitting...' : 'Submit Feedback'}
                </Button>
            </div>
        </NiceModal>
    );
}
